import React, { PropsWithChildren, PureComponent, ReactNode } from 'react';
import { SidePanelSeparator } from '../../components/common/SidePanelSeparator';
import { Flex } from '../../components/common/Flex';
import './ExtensiblePanel.less';
import { Spin } from 'antd';
import Utils from 'app/common/util/Utils';

export interface ExtensiblePanelProps {
    onWidthChange?: (modalWidth: string) => void;
}

export interface ExtensiblePanelState {
    panelIdxVisible: number;
    showSeparator: boolean;
    loading?: boolean;
}

/**
 * This abstract component can be derived
 */
export default abstract class ExtensiblePanel<
    P extends ExtensiblePanelProps = ExtensiblePanelProps,
    S extends ExtensiblePanelState = ExtensiblePanelState,
> extends PureComponent<PropsWithChildren<P>, S> {
    abstract getPanels(): Map<ReactNode, number>;

    constructor(props) {
        super(props);
        this.state = {
            panelIdxVisible: 0,
            showSeparator: true,
            loading: false,
        } as S;
    }

    getWidth = (fromIdx: number = 0) => {
        return (
            this.getPanels() &&
            Array.from(this.getPanels().values())
                .filter(
                    (value, idx) =>
                        idx >= fromIdx &&
                        idx <= this.state.panelIdxVisible &&
                        Utils.isExists(Array.from(this.getPanels().keys())[idx]),
                )
                .reduce((a, b) => a + b, 0)
        );
    };

    getUnit = () => {
        return 'em';
    };

    showSidePanel = (panelIdx: number = 1) => {
        this.setState(
            {
                panelIdxVisible: panelIdx,
            },
            () => this.props.onWidthChange?.(this.getWidth() + this.getUnit()),
        );
    };

    hideSidePanel = (pandelIdx: number = 1) => {
        this.setState(
            {
                panelIdxVisible: pandelIdx - 1,
            },
            () => this.props.onWidthChange?.(this.getWidth() + this.getUnit()),
        );
    };

    readonly renderMainPanel = () => {
        if (!this.getPanels()?.size) return undefined;

        const idx = 0;
        const content = Array.from(this.getPanels().keys())[idx];
        return (
            <Flex shrink={false} className="extensible-panel-content">
                {content}
            </Flex>
        );
    };
    readonly renderVisibleSidePanels = () => {
        if (!this.getPanels()?.size || this.state.panelIdxVisible === 0) return undefined;

        const sidePanels = [];

        Array.from(this.getPanels().keys()).forEach((content, idx) => {
            if (idx > 0 && idx <= this.state.panelIdxVisible && Utils.isExists(content)) {
                const width = Array.from(this.getPanels().values())[idx];
                sidePanels.push(
                    <Flex key={`side-panel-${idx}`} shrink={false} grow={true} size={width + this.getUnit()}>
                        {this.renderSidePanel(content)}
                    </Flex>,
                );
            }
        });
        return <Flex size={this.getWidth(1) + this.getUnit()}>{sidePanels}</Flex>;
    };
    readonly renderSidePanel = (content: ReactNode) => {
        return (
            <Flex>
                {this.state.showSeparator ? (
                    <SidePanelSeparator key="side-panel-separator" onHideSidePanel={this.hideSidePanel} />
                ) : undefined}
                <Flex key="side-panel-content" className="side-panel-content" style={{ marginRight: '1em' }}>
                    {content}
                </Flex>
            </Flex>
        );
    };
    readonly render = () => {
        return (
            <Spin wrapperClassName="reply-panel-spinner" spinning={this.state.loading} tip="Loading data">
                <Flex className="extensible-panel" size={this.getWidth() + this.getUnit()}>
                    {this.renderMainPanel()}
                    {this.renderVisibleSidePanels()}
                </Flex>
            </Spin>
        );
    };
}
